// src/hooks/useWinningSchedulesHook.js
import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  archiveInstantWin as archiveInstantWinMutation,
  createInstantWin as createInstantWinMutation,
  deleteInstantWin as deleteInstantWinMutation,
  updateInstantWin as updateInstantWinMutation,
} from "../graphql/mutations/instantWinMutations";
import { listInstantWins } from "../graphql/queries/instantWinQueries";

export const useWinningSchedulesHook = (isArchived = false) => {
  // Pagination (client-side)
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [items, setItems] = useState([]); // current page items
  const [totalCount, setTotalCount] = useState(0);

  // Filters (mirror leaderboards pattern)
  const filters = useRef({
    title: "",
    instantWinStatus: "",
    startsAt: "",
    endsAt: "",
    campaignInstantWinId: "",
  });

  // Keep a reference to all items
  const allRef = useRef([]);

  // Fetch all winning schedules (large page size for client-side filtering)
  const { data, loading, refetch, error } = useQuery(listInstantWins, {
    variables: { page: 1, size: 200, filter: {} },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    const list = data?.listInstantWins?.items || [];
    allRef.current = [...list];
    applyFilters(1, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Mutations with refetch of main query
  const [createInstantWin, { loading: createLoading }] = useMutation(
    createInstantWinMutation
  );
  const [updateInstantWin, { loading: updateLoading }] = useMutation(
    updateInstantWinMutation
  );
  const [deleteInstantWin, { loading: deleteLoading }] = useMutation(
    deleteInstantWinMutation,
    { onCompleted: () => refetch?.() }
  );
  const [archiveInstantWin, { loading: archiveLoading }] = useMutation(
    archiveInstantWinMutation,
    { onCompleted: () => refetch?.() }
  );

  const normalizeStatus = (val) => {
    // Dropdown may return string or array of objects
    if (Array.isArray(val)) return val[0]?.id ?? "";
    if (typeof val === "object" && val) return val.id ?? "";
    return val ?? "";
  };

  const applyFilters = (targetPage = page, pageSize = size) => {
    const { title, instantWinStatus, startsAt, endsAt, campaignInstantWinId } =
      filters.current;
    const status = normalizeStatus(instantWinStatus);
    const campaignInstantWinIds = normalizeStatus(campaignInstantWinId);
    const base = allRef.current.filter((it) =>
      isArchived
        ? it.instantWinStatus === "ARCHIVED"
        : it.instantWinStatus !== "ARCHIVED"
    );
    const filtered = base.filter((it) => {
      // title contains (case-insensitive)
      if (
        title &&
        !`${it.title || ""}`.toLowerCase().includes(title.toLowerCase())
      ) {
        return false;
      }
      // status exact match
      if (status && it.instantWinStatus !== status) return false;
      // date window
      if (startsAt) {
        try {
          if (new Date(it.startsAt).getTime() < new Date(startsAt).getTime())
            return false;
        } catch (e) {
          console.log(e);
        }
      }
      if (endsAt) {
        try {
          if (new Date(it.endsAt).getTime() > new Date(endsAt).getTime())
            return false;
        } catch (e) {
          console.log(e);
        }
      }
      // campaign instant win id
      if (campaignInstantWinIds) {
        return it.id === campaignInstantWinIds;
      }
      return true;
    });

    const total = filtered.length;
    const startIdx = (targetPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const currentSlice = filtered.slice(startIdx, endIdx);

    setTotalCount(total);
    setItems(currentSlice);
    setPage(targetPage);
    setSize(pageSize);
  };

  useEffect(() => {
    if (allRef.current.length) {
      applyFilters(1, size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArchived, size]);

  const onChangeFilters = () => {
    applyFilters(1, size);
  };

  const onPageChange = (newPage) => {
    applyFilters(newPage, size);
  };

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil((totalCount || 0) / (size || 1))),
    [totalCount, size]
  );

  return {
    // data
    items,
    totalCount,
    loading,
    error,
    allList: allRef.current,
    // pagination
    page,
    size,
    pageCount,
    onPageChange,
    // filters
    filters,
    onChangeFilters,
    // mutations
    createInstantWin,
    updateInstantWin,
    deleteInstantWin,
    createLoading,
    updateLoading,
    deleteLoading,
    archiveInstantWin,
    archiveLoading,
    // utils
    refetch,
  };
};
